<template>
  <div
    :class="{ submitted: formSubmitted }"
    data-component="form"
    data-component-variant="formCode"
  >
    <ContentfulFormRegister
      v-if="formCode == 'register'"
      @success="submitted"
      :formCode="formCode"
    />
    <SalesforceSalesForm
      v-if="formCode === 'corporate'"
      :thank-you-page="thankYouUrl"
    />
    <SalesforcePrivacyForm
      v-if="formCode === 'privacy'"
      :thank-you-page="thankYouUrl"
    />
    <SalesforceGroupsForm
      v-if="formCode === 'groups'"
      :hotels="hotelOptions"
      :thank-you-page="thankYouUrl"
    />

    <div class="submittedInfo" v-if="formSubmitted">
      <p class="small">
        {{ $t("website.general.formSubmitted") }}
      </p>
    </div>
  </div>
</template>
<script setup lang="ts">
// Vendor
import type { Entry } from "contentful";

import { getSlugForPagetype } from "~/services/slugService";

const { locale } = useI18n();
const router = useRouter();
const localePath = useLocalePath();
const { $contentfulClient, $contentfulMarketingClient } = useNuxtApp();
const { public: publicConfig } = useRuntimeConfig();
const { hotels } = useHotels();

const formSubmitted = ref(false);

/**
 * Salesforce only accepts the english values of the hotel names.
 * We must alter the hotels list to set the values to the english names.
 */
const { data: salesforceHotelValues, status: salesforceHotelValuesStatus } =
  await useFetch("/api/v2/properties/hotels", {
    key: "salesforce-hotel-values",
    query: {
      locale: "en",
    },
    transform: (data: PropertyHotel[]) => {
      if (data && Array.isArray(data)) {
        return data.map(({ name, propertyCode }) => ({
          name,
          propertyCode,
        }));
      }

      return [];
    },
  });

function processSalesforceHotelName(hotelName?: string) {
  return hotelName?.replace(/hotel/, "");
}

const hotelOptions = computed(() =>
  salesforceHotelValuesStatus.value === "success"
    ? hotels.value.map((hotel) => {
        const salesforceHotelMatch = salesforceHotelValues.value?.find(
          (salesforceHotel) =>
            salesforceHotel.propertyCode === hotel.propertyCode,
        );

        return {
          label: processSalesforceHotelName(hotel.name),
          value: `citizenM ${processSalesforceHotelName(salesforceHotelMatch?.name)}`,
        };
      })
    : [],
);

const props = defineProps({
  formCode: String,
  thankYouPage: Array,
});

const unifiedFormCode = computed(() => props.formCode?.replaceAll(" ", "-"));
const { data: thankYouUrl } = await useAsyncData(
  `form-thank-you-${unifiedFormCode.value}-${locale.value}`,
  async () => {
    //@ts-expect-error `props.thankYouPage` is typed as an array, but we expect it to be an array of Entries.
    const slug = props?.thankYouPage?.[0]?.fields?.slug;
    if (slug) {
      return slug;
    }
    return await getSlugForPagetype(
      (props?.thankYouPage?.[0] as Entry).sys.id,
      locale.value,
      $contentfulMarketingClient,
      $contentfulClient,
      publicConfig,
      localePath,
    ).catch(() => null);
  },
);

const submitted = () => {
  // Redirect to thank you page
  if (thankYouUrl?.value) {
    //window.location = thankYouUrl.value;
    router.push(thankYouUrl.value);
  } else {
    useSnackbarStore().success({
      text: "Thank you! We received your request.",
      timeout: false,
    });
  }
};
</script>

<style lang="scss" scoped>
/* stylelint-disable selector-class-pattern */

.submittedInfo {
  position: absolute;
  top: 4rem;
  left: 50%;
  text-align: center;
  background: var(--primary-white);
  border: 1px var(--color-green) solid;
  border-radius: var(--border-radius);
  transform: translateX(-50%);
  opacity: 0;
  transition: var(--transition-opacity);
  content: "already submitted";
  pointer-events: none;

  p {
    padding: 0 2em;
  }

  .submitted & {
    opacity: 1;
    pointer-events: auto;
  }
}

.submitted {
  position: relative;

  :deep(form) {
    opacity: 0.4;
    pointer-events: none;
  }
}
</style>
